/**
 * Bulk Invalidate Dialog Component
 *
 * Modal dialog for selecting and invalidating multiple cache namespaces.
 */

import { useState } from 'react';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { CacheNamespace, CacheNamespaceInfo } from '@/types/cache';
import { CACHE_NAMESPACE_LABELS } from '@/types/cache';

interface BulkInvalidateDialogProps {
  namespaces: CacheNamespaceInfo[];
  onInvalidate: (namespaces: CacheNamespace[]) => void;
  isInvalidating: boolean;
}

/**
 * Bulk Invalidate Dialog
 * Multi-select dialog for invalidating multiple namespaces
 */
export function BulkInvalidateDialog({
  namespaces,
  onInvalidate,
  isInvalidating,
}: BulkInvalidateDialogProps) {
  const [selectedNamespaces, setSelectedNamespaces] = useState<CacheNamespace[]>([]);
  const [open, setOpen] = useState(false);

  const totalKeys = namespaces
    .filter(ns => selectedNamespaces.includes(ns.name))
    .reduce((sum, ns) => sum + ns.count, 0);

  const handleToggle = (namespace: CacheNamespace) => {
    setSelectedNamespaces(prev =>
      prev.includes(namespace) ? prev.filter(n => n !== namespace) : [...prev, namespace]
    );
  };

  const handleSelectAll = () => {
    if (selectedNamespaces.length === namespaces.length) {
      setSelectedNamespaces([]);
    } else {
      setSelectedNamespaces(namespaces.map(ns => ns.name));
    }
  };

  const handleConfirm = () => {
    if (selectedNamespaces.length > 0) {
      onInvalidate(selectedNamespaces);
      setOpen(false);
      setSelectedNamespaces([]);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedNamespaces([]);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" className="gap-2">
          <Trash2 className="h-4 w-4" />
          Bulk Invalidate
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Bulk Cache Invalidation
          </AlertDialogTitle>
          <AlertDialogDescription>
            Select the cache namespaces you want to invalidate. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              {selectedNamespaces.length === namespaces.length ? 'Deselect All' : 'Select All'}
            </Button>
            {selectedNamespaces.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {totalKeys.toLocaleString()} keys will be deleted
              </span>
            )}
          </div>

          <div className="space-y-3">
            {namespaces.map(ns => (
              <div key={ns.name} className="flex items-center space-x-3">
                <Checkbox
                  id={ns.name}
                  checked={selectedNamespaces.includes(ns.name)}
                  onCheckedChange={() => handleToggle(ns.name)}
                  disabled={ns.count === 0}
                />
                <Label
                  htmlFor={ns.name}
                  className={`flex-1 flex items-center justify-between cursor-pointer ${
                    ns.count === 0 ? 'opacity-50' : ''
                  }`}
                >
                  <span>{CACHE_NAMESPACE_LABELS[ns.name]}</span>
                  <span className="text-sm text-muted-foreground">
                    {ns.count.toLocaleString()} keys
                  </span>
                </Label>
              </div>
            ))}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isInvalidating}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={selectedNamespaces.length === 0 || isInvalidating}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isInvalidating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Invalidating...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Invalidate ({selectedNamespaces.length})
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
